// =====================================================
// Shared: Nav, Footer, common widgets
//
// Phase-14.5 C4: dropped @ts-nocheck. Components were the priority
// (Nav + dropdowns drive every page); the SVG helpers further down
// (Sparkline, CognitiveRadar, KnowledgeGraph, ArchStack) got
// function-signature typing but not deep type-safety on every SVG
// path expression — that's pure-render math, not RBAC-critical.
// =====================================================
import React from "react";
import { Icon } from "./icons";
import { useRole, ROLES, type RoleId, type Role } from "./role";
import { useAuth, type AuthContextValue } from "./auth/AuthContext";
import type { Actions, Subjects } from "./auth/ability";
import { Button } from "./ui";
import { Avatar } from "./ui/Avatar";
import type { Go } from "./router";
import { OrgAttribution } from "./components/OrgAttribution";
import { useNotifications, notifLink, type UseNotifications } from "./hooks/useNotifications";
import { useConnectSummary } from "./hooks/useConnectSummary";
import { useChatUnread } from "./hooks/useChatUnread";
import { useTerm } from "./term-context";
import { isRetiredRoute } from "./config/adapt-flags";

// (Window.toast / Window.confirmAction / Window.openCommandPalette
// types live in ui.tsx — the file that actually assigns them. Both
// declarations would merge if duplicated; keeping a single source.)

interface NavItem {
  id: string;
  label: string;
  /** Marks routes that hit the real API; renders the pulse dot. */
  live?: boolean;
  /**
   * R-NAV-1 — optional CASL gate. When present, the item only renders if
   * the current user's ability allows `action` on `subject`. This is the
   * "filter nav by ability.can" wiring (spec §2.7-4); the full per-item
   * tagging of the redesigned sidebar lands in R-NAV-2.
   */
  can?: { action: Actions; subject: Subjects };
}

// R-NAV-1 (spec §3.1.3, §2.5) — anonymous visitors see PUBLIC nav links,
// never a role's workspace links (which would bounce to /login and leak a
// "logged-in" feel). Only routes classified PUBLIC are listed so no link
// dead-ends at the auth gate. (Catalog becomes public in R-NAV-3; it
// joins this list then.)
const PUBLIC_NAV_ITEMS: NavItem[] = [
  { id: "programs", label: "برنامه‌ها" },
  // R-MARKET P4 — «بازار دانش» PUBLIC marketplace. Reachable LOGGED-OUT (doc §7.1:
  // every role + a guest can browse), so it belongs in the guest/public top nav.
  // route-classification marks /market PUBLIC, so this link never dead-ends at
  // the auth gate. `live` (it talks to the public catalog API).
  { id: "market", label: "بازار پکیج آموزشی", live: true },
  // «درخواست پذیرش» → the real public /apply admissions funnel (live API). Surfaced
  // as a first-class public nav link (not just the «ثبت‌نام» CTA button) so an
  // anonymous visitor can discover admissions directly. /apply is classified PUBLIC,
  // so the link never bounces to the auth gate.
  { id: "apply", label: "درخواست پذیرش", live: true },
  { id: "about", label: "درباره" },
  { id: "pricing", label: "پلن‌ها" },
  { id: "help", label: "راهنما" },
];

// Nav highlights the LIVE routes that actually talk to the API (Phase 4-9).
// Mocked routes from the original SPA still hash-route alongside but are
// no longer the default landing surface for any role.
const NAV_ITEMS_BY_ROLE: Record<RoleId, NavItem[]> = {
  finance_manager: [
    { id: "admin/finance/treasury", label: "خزانه", live: true },
    { id: "admin/finance/payroll", label: "اجرای حقوق", live: true },
    { id: "admin/finance/decrees", label: "احکام", live: true },
    { id: "admin/finance/claims", label: "حق‌التدریس", live: true },
  ],
  student: [
    { id: "progress", label: "پیشرفت من", live: true },
    { id: "catalog", label: "کاتالوگ", live: true },
    { id: "my-courses", label: "دوره‌های من", live: true },
    { id: "tutor", label: "دستیار AI", live: true },
    { id: "calendar", label: "تقویم" },
    { id: "community", label: "جامعه" },
  ],
  instructor: [
    { id: "progress", label: "میز کار", live: true },
    { id: "my-courses", label: "دروس من", live: true },
    { id: "admin/course-content", label: "تولید درس" },
    { id: "gradebook", label: "دفتر نمره", live: true },
    { id: "classroom", label: "کلاس زنده" },
    { id: "messages", label: "پیام‌ها" },
    { id: "tutor", label: "دستیار AI", live: true },
  ],
  admin: [
    { id: "progress", label: "داشبورد", live: true },
    { id: "admin", label: "مدیریت" },
    { id: "analytics", label: "تحلیل" },
    { id: "schools", label: "دانشکده‌ها" },
    { id: "events", label: "رویدادها" },
    { id: "tutor", label: "دستیار AI", live: true },
  ],
  // R-NAV-2 — faculty_admin (scope-bound). Links to the academic surfaces
  // they manage within their own faculty.
  faculty_admin: [
    { id: "faculty-dashboard", label: "داشبورد دانشکده" },
    { id: "admin/departments", label: "گروه‌ها" },
    { id: "admin/programs", label: "رشته‌ها" },
    { id: "admin/offerings", label: "ارائه دروس" },
    { id: "admin/enrollments", label: "ثبت‌نام‌ها" },
    { id: "messages", label: "پیام‌ها" },
  ],
  // Parent role: lands on the parent dashboard first. The public "home"
  // route was the first item which felt jarring for a logged-in parent;
  // now we lead with the parent dashboard and drop "home" — parents
  // reach the public site via the logo in the brand area if needed.
  parent: [
    { id: "parent", label: "میز کار" },
    { id: "calendar", label: "تقویم فرزند" },
    { id: "credential", label: "گواهی‌ها" },
    { id: "messages", label: "پیام‌ها" },
    { id: "help", label: "پشتیبانی" },
  ],
  org: [
    { id: "org", label: "میز سازمان" },
    { id: "analytics", label: "تحلیل تیم" },
    { id: "faculty", label: "مربیان" },
    { id: "events", label: "رویدادها" },
    { id: "pricing", label: "پلن‌ها" },
    { id: "help", label: "پشتیبانی" },
  ],
  // Phase-15 R7: nav for the 5 roles added in R1's seed.
  ta: [
    { id: "classroom", label: "کلاس", live: true },
    { id: "my-courses", label: "دوره‌های من", live: true },
    { id: "progress", label: "پیشرفت", live: true },
    { id: "tutor", label: "دستیار AI", live: true },
  ],
  content_manager: [
    { id: "catalog", label: "کاتالوگ", live: true },
    { id: "admin/course-content", label: "تولید محتوا" },
    { id: "analytics", label: "تحلیل" },
    { id: "messages", label: "پیام‌ها" },
    { id: "help", label: "پشتیبانی" },
  ],
  // S-NRI-ADAPT — «سازندهٔ پکیج» (استودیو دانش): میز کار سازنده + دوره‌ساز + بازار.
  creator: [
    { id: "creator-studio", label: "میز کار" },
    { id: "market/studio", label: "دوره‌ساز" },
    { id: "market", label: "بازار", live: true },
  ],
  support: [
    { id: "support", label: "میز پشتیبانی" },
    { id: "audit", label: "گزارش حسابرسی", live: true, can: { action: "read", subject: "AuditLog" } },
    { id: "messages", label: "پیام‌ها" },
    { id: "help", label: "پشتیبانی" },
  ],
  moderator: [
    { id: "community", label: "جامعه" },
    { id: "messages", label: "پیام‌ها" },
    { id: "help", label: "پشتیبانی" },
  ],
  super_admin: [
    { id: "progress", label: "داشبورد", live: true },
    { id: "admin", label: "مدیریت" },
    { id: "audit", label: "گزارش حسابرسی", live: true, can: { action: "read", subject: "AuditLog" } },
    { id: "analytics", label: "تحلیل" },
    { id: "tutor", label: "دستیار AI", live: true },
  ],
  // WELL-4 — counselor (مشاور دانشگاه). Least-privilege wellness desk only.
  counselor: [
    { id: "counselor-inbox", label: "میز کار" },
    { id: "counseling-appointments", label: "نوبت‌های مشاوره" },
    { id: "crisis-queue", label: "موارد بحرانی" },
    { id: "messages", label: "پیام‌ها" },
  ],
  // S-NRI-3 — org/oversight roles. READ surfaces only (existing route ids); no
  // new pages beyond the already-built /admin/organizations. admin/users +
  // admin/roles are deliberately NOT surfaced here.
  //
  // holding_manager / company_admin / company_training_manager — org managers:
  // dashboard + organizational structure + reporting (analytics).
  holding_manager: [
    { id: "holding-dashboard", label: "داشبورد", live: true },
    { id: "admin/organizations", label: "ساختار سازمانی", live: true },
    { id: "analytics", label: "تحلیل" },
  ],
  company_admin: [
    { id: "company-dashboard", label: "داشبورد", live: true },
    { id: "admin/organizations", label: "ساختار سازمانی", live: true },
    { id: "analytics", label: "تحلیل" },
  ],
  company_training_manager: [
    { id: "training-dashboard", label: "داشبورد", live: true },
    { id: "admin/organizations", label: "ساختار سازمانی", live: true },
    { id: "analytics", label: "تحلیل" },
  ],
  // certification_officer — credential issuance desk.
  certification_officer: [
    { id: "certification-desk", label: "داشبورد", live: true },
    { id: "credential", label: "گواهی‌ها" },
  ],
  // practical_assessor — minimal read surface (no practicum pages yet, S-NRI-8).
  practical_assessor: [
    { id: "assessor-dashboard", label: "داشبورد", live: true },
    { id: "my-courses", label: "دوره‌های من", live: true },
  ],
  // ministry_supervisor & auditor — read-only oversight. auditor additionally
  // sees «گزارش حسابرسی» (CASL-gated read:AuditLog, like support/super_admin).
  ministry_supervisor: [
    { id: "ministry-dashboard", label: "داشبورد", live: true },
    { id: "admin/organizations", label: "ساختار سازمانی", live: true },
    { id: "analytics", label: "تحلیل" },
  ],
  auditor: [
    { id: "audit-dashboard", label: "داشبورد", live: true },
    { id: "admin/organizations", label: "ساختار سازمانی", live: true },
    { id: "analytics", label: "تحلیل" },
    { id: "audit", label: "گزارش حسابرسی", live: true, can: { action: "read", subject: "AuditLog" } },
  ],
};

export type NavMode = "public" | "workspace" | "auth_flow";

export interface NavProps {
  current: string;
  go: Go;
  /**
   * Phase-A R1.1: route-kind-aware mode set by AppShell.
   *   public    — top-nav links + own hamburger drawer for those links
   *               (existing behavior; default)
   *   workspace — top-nav links hidden; the single hamburger calls
   *               `onWorkspaceMenuClick` which AppShell wires to its
   *               sidebar Sheet drawer
   *   auth_flow — minimal chrome: brand + theme toggle only; no nav
   *               links, no hamburger, no notifications/user popovers
   */
  mode?: NavMode;
  /** Workspace-only: AppShell-provided callback to open the sidebar drawer. */
  onWorkspaceMenuClick?: () => void;
  /**
   * Workspace-only: whether the sidebar Sheet drawer is currently open.
   * Wired to `aria-expanded` on the hamburger trigger (R7.5). Replaces
   * the earlier `aria-controls="appshell-sidebar-drawer"` which axe-core
   * flagged as `aria-valid-attr-value` on every workspace route (the
   * referenced id wasn't in the DOM because Radix Sheet lazy-mounts).
   */
  workspaceMenuOpen?: boolean;
}

// R-SIS-3d (§3.1.5) — roles that see the navbar term switcher (academic ones).
const ACADEMIC_TERM_ROLES = new Set<RoleId>([
  "student",
  "instructor",
  "faculty_admin",
  "ta",
  "admin",
  "super_admin",
]);

export const Nav = ({
  current,
  go,
  mode = "public",
  onWorkspaceMenuClick,
  workspaceMenuOpen = false,
}: NavProps): React.ReactElement => {
  const { role, setRole, availableRoles } = useRole();
  const auth = useAuth();
  // R-NAV-5 — real notifications (bell badge + dropdown). No-op for anon.
  const notif = useNotifications({ limit: 10 });
  // R-CONNECT — pending incoming connection requests → «شبکه» nav badge. No-op
  // for anon. Mounted once for the whole authed session (the Nav is too).
  const connect = useConnectSummary();
  // R-CHAT M4 — total unread chat messages → «پیام‌ها» nav badge. No-op for anon;
  // polls ~15s + live-bumps on socket "message".
  const chatUnread = useChatUnread();
  // R-SIS-3d (§3.1.5) — term switcher: academic roles only, workspace + authed.
  const { terms, selectedTermId, setSelectedTermId } = useTerm();
  const showTermSwitcher =
    !!auth.user &&
    mode === "workspace" &&
    terms.length > 0 &&
    ACADEMIC_TERM_ROLES.has(role.id);
  const [open, setOpen] = React.useState(false);
  const [notifsOpen, setNotifsOpen] = React.useState(false);
  const [userOpen, setUserOpen] = React.useState(false);
  React.useEffect(() => { setOpen(false); setNotifsOpen(false); setUserOpen(false); }, [current]);
  const isWorkspace = mode === "workspace";
  const isAuthFlow = mode === "auth_flow";

  // Logo destination is mode + auth aware. WORKSPACE always → dashboard;
  // PUBLIC: dashboard if logged in (back-to-workspace shortcut), else home;
  // AUTH_FLOW: home (escape back to the marketing site).
  const brandTarget: string = isWorkspace
    ? "dashboard"
    : isAuthFlow
      ? "home"
      : auth.isAuthenticated
        ? "dashboard"
        : "home";

  // close popovers on outside click
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target?.closest(".notif-wrap")) setNotifsOpen(false);
      if (!target?.closest(".user-wrap")) setUserOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Esc closes the mobile drawer + any popover.
  React.useEffect(() => {
    if (!open && !notifsOpen && !userOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setNotifsOpen(false);
        setUserOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, notifsOpen, userOpen]);

  // Body-scroll lock while the mobile drawer is open. Without this the
  // page below can be scrolled with two-finger gestures while the drawer
  // is visible, which feels broken on iOS Safari.
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("nav-drawer-open", open);
    return () => document.body.classList.remove("nav-drawer-open");
  }, [open]);

  // R-NAV-1: authed → the active role's links, CASL-filtered; anonymous →
  // public links only (no role workspace leak). role.id === activeRoleId
  // for an authed user (role derives from the session now).
  const items: NavItem[] = auth.isAuthenticated
    ? (NAV_ITEMS_BY_ROLE[role.id] || NAV_ITEMS_BY_ROLE.student).filter(
        (n) => !n.can || auth.ability.can(n.can.action, n.can.subject),
      )
    : PUBLIC_NAV_ITEMS;

  return (
    <nav className="nav" data-mode={mode}>
      <div className="nav-inner">
        {/* Phase-A R6.6 — RTL navbar correctness:
            WORKSPACE renders the hamburger as the FIRST child of
            nav-inner so under RTL it sits at the START edge (right in
            Persian), matching the standard Persian sidebar pattern
            (menu opener on the right, logo next to it, user/theme/
            notification chips on the left). The PUBLIC mode keeps the
            hamburger inside `.nav-actions` at the end (left in RTL)
            because that's the established mobile pattern there — the
            user is opening a drawer of nav-links that fly out from the
            same edge as the brand. AUTH_FLOW has no hamburger. */}
        {isWorkspace ? (
          <button
            id="appshell-sidebar-trigger"
            type="button"
            className="nav-toggle nav-toggle-start"
            onClick={() => onWorkspaceMenuClick?.()}
            aria-label="منو"
            // R7.5 — disclosure-widget ARIA pattern:
            // aria-controls dropped because Radix Sheet lazy-mounts the
            // drawer, so the IDREF can't always resolve (axe-core
            // aria-valid-attr-value rule fired on all 53 workspace
            // routes). aria-expanded + aria-haspopup="dialog" is the
            // canonical pattern recommended by ARIA Authoring Practices
            // for buttons that open a dialog/menu — and it's the pattern
            // Radix wires automatically when you use <SheetTrigger>,
            // which we can't here because the trigger lives in Nav
            // (rendered by AppShell at a different tree depth than the
            // Sheet itself).
            aria-expanded={workspaceMenuOpen}
            aria-haspopup="dialog"
          >
            <span className="bars"></span>
          </button>
        ) : null}

        <a href={"/" + brandTarget} onClick={(e) => { e.preventDefault(); go(brandTarget); }} className="brand">
          {/* S-NRI-1 — نشان پژوهشگاه نیرو (institutional logo) as the navbar
              brand mark. Visible on all routes that render the global AppShell
              Nav (every route except `/` which uses Home's own .home-nav-v2).
              Small WebP variant; the full-size PNG stays on disk. */}
          <img
            src="/tenants/nri/pajooheshgah-192.webp"
            alt="پژوهشگاه نیرو"
            width="40"
            height="40"
            style={{ width: 40, height: 40, objectFit: "contain", display: "block" }}
          />
          <span>
            پژوهشگاه نیرو
            <div className="brand-sub">آموزش ضمن خدمت صنعت آب و برق</div>
          </span>
        </a>

        {/* Top-nav links: PUBLIC only. WORKSPACE relies on the sidebar
            for in-app navigation, AUTH_FLOW shows a minimal shell. */}
        {mode === "public" && (
          <div className={"nav-links " + (open ? "open" : "")}>
            {items.map((n) => (
              <a
                key={n.id}
                href={"#" + n.id}
                onClick={(e) => { e.preventDefault(); go(n.id); setOpen(false); }}
                className={"nav-link " + (current === n.id ? "active" : "")}
                aria-current={current === n.id ? "page" : undefined}
              >
                {n.label}
                {n.live && (
                  <span
                    className="nav-live-dot"
                    aria-label="داده زنده"
                    title="متصل به API"
                  />
                )}
              </a>
            ))}
          </div>
        )}

        <div className="nav-actions">
          {/* R-SIS-3d (§3.1.5) — term switcher (academic roles, workspace).
              Registration always uses the ACTIVE term server-side; the
              selection drives term-aware views (transcript, schedule). */}
          {showTermSwitcher && (
            <select
              className="nav-term-switcher hide-on-xs"
              value={selectedTermId ?? ""}
              onChange={(e) => setSelectedTermId(e.target.value)}
              aria-label="انتخاب نیم‌سال"
              title="نیم‌سال"
              style={{
                fontFamily: "inherit",
                fontSize: 13,
                padding: "6px 10px",
                borderRadius: 10,
                background: "var(--surface)",
                color: "var(--fg)",
                border: "1px solid var(--line-2)",
                maxWidth: 180,
              }}
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.title}
                  {t.status === "ACTIVE" ? " ●" : ""}
                </option>
              ))}
            </select>
          )}

          {/* Command palette trigger.
              Phase-16 Gate-2 smoke: hidden at <sm because the 5-icon
              cluster + brand exceeds 320 px after R7' enforced 44 px
              touch targets. Keyboard users still get it via Ctrl/Cmd+K;
              touch users find it inside the hamburger drawer. */}
          <button
            className="nav-icon-btn hide-on-xs"
            onClick={() => window.openCommandPalette?.()}
            aria-label="پالت دستورات (Cmd+K)"
            title="جستجوی سریع (Ctrl+K)"
          >
            <Icon name="search" size={18} />
          </button>

          {/* Theme toggle removed (D-2026-06-13, owner): dark mode is disabled
              for now — the site is forced light in main.tsx. Re-add this button
              when dark returns. */}

          {/* R-CONNECT — «شبکه» quick-access with a pendingIncoming badge.
              Authed only (no connect state for a guest). Clicking opens the
              network hub (/network). Hidden at <xs for the same 320-px width
              constraint as the other icon chips. */}
          {/* S-NRI-ADAPT — چیپ «شبکه» فقط وقتی ماژول network بازنشسته نباشد. */}
          {auth.user && !isRetiredRoute("network") && (
            <button
              className="nav-icon-btn hide-on-xs"
              style={{ position: "relative" }}
              onClick={() => go("network")}
              aria-label={
                connect.pendingIncoming > 0
                  ? `شبکه (${connect.pendingIncoming} درخواست جدید)`
                  : "شبکه"
              }
              title="شبکهٔ من"
            >
              <Icon name="users" size={18} />
              {connect.pendingIncoming > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 0, insetInlineEnd: 0,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "var(--accent)", color: "#fff",
                    fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                  }}
                >
                  {connect.pendingIncoming > 99 ? "۹۹+" : toFa(connect.pendingIncoming)}
                </span>
              )}
            </button>
          )}

          {/* R-CHAT M4 — «پیام‌ها» quick-access with an unread badge. Authed only;
              clicking opens the messenger (/messages). Hidden at <xs for the same
              320-px width constraint as the other icon chips. */}
          {auth.user && (
            <button
              className="nav-icon-btn hide-on-xs"
              style={{ position: "relative" }}
              onClick={() => go("messages")}
              aria-label={
                chatUnread.totalUnread > 0
                  ? `پیام‌ها (${chatUnread.totalUnread} پیام نخوانده)`
                  : "پیام‌ها"
              }
              title="پیام‌ها"
            >
              <Icon name="chat" size={18} />
              {chatUnread.totalUnread > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute", top: 0, insetInlineEnd: 0,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8, background: "var(--accent)", color: "#fff",
                    fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                  }}
                >
                  {chatUnread.totalUnread > 99 ? "۹۹+" : toFa(chatUnread.totalUnread)}
                </span>
              )}
            </button>
          )}

          {/* Notifications — authed only. R-NAV-1 (spec §2.5/§3.1):
              an anonymous visitor sees no bell (no notifications without a
              session). R-NAV-5 wires this to the real /v1/notifications.
              Phase-16 Gate-2 smoke: also `.hide-on-xs` for the same
              320-px width constraint. */}
          {auth.user && (
            <div className="notif-wrap relative hide-on-xs" >
              <button
                className="nav-icon-btn"
                style={{ position: "relative" }}
                onClick={() => setNotifsOpen(!notifsOpen)}
                aria-label={notif.unreadCount > 0 ? `اعلان‌ها (${notif.unreadCount} نخوانده)` : "اعلان‌ها"}
              >
                <Icon name="bell" size={18} />
                {notif.unreadCount > 0 && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: "absolute", top: 0, insetInlineEnd: 0,
                      minWidth: 16, height: 16, padding: "0 4px",
                      borderRadius: 8, background: "var(--accent)", color: "#fff",
                      fontSize: 9, fontWeight: 700, lineHeight: "16px", textAlign: "center",
                    }}
                  >
                    {notif.unreadCount > 99 ? "۹۹+" : toFa(notif.unreadCount)}
                  </span>
                )}
              </button>
              {notifsOpen && (
                <NotificationsDropdown go={go} notif={notif} onClose={() => setNotifsOpen(false)} />
              )}
            </div>
          )}

          {/* R-NAV-1 (spec §2.5, §3.1.13) — authed: avatar + name + user
              menu. Anonymous: «ورود» / «ثبت‌نام» buttons, NO user menu, NO
              role identity. This is the core fix for "logout looks
              logged-in": an anonymous visitor can no longer see a role
              avatar/name/menu derived from the default student role. */}
          {auth.user ? (
            <div className="user-wrap relative" >
              <button
                className="user-btn"
                onClick={() => setUserOpen(!userOpen)}
                aria-label="منوی کاربر"
              >
                <Avatar
                  seed={auth.user.userId}
                  role={role.id}
                  name={auth.user.fullName || auth.user.email}
                  size={32}
                />
                <span className="user-name">{
                  auth.user.fullName?.split(" ")[0]
                    || auth.user.email?.split("@")[0]
                    || role.label
                }</span>
              </button>
              {userOpen && (
                <UserDropdown go={go} role={role} setRole={setRole} auth={auth} availableRoles={availableRoles} />
              )}
            </div>
          ) : (
            // Anonymous CTA. Hidden in AUTH_FLOW (you're already on
            // login/register — redundant). «ورود» is intentionally NOT
            // "ورود به حساب" (the form-submit string) to avoid colliding
            // with the Playwright login helper's button matcher.
            !isAuthFlow && (
              <div className="auth-cta flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => go("login")}>
                  ورود
                </Button>
                {/* R-AUTH-1: application-only — «ثبت‌نام» now leads to the
                    real /apply funnel (open self-registration retired). */}
                <Button variant="primary" size="sm" onClick={() => go("apply")}>
                  ثبت‌نام
                </Button>
              </div>
            )
          )}

          {/* Hamburger button — PUBLIC mode only.
              WORKSPACE renders its own hamburger at the start of
              nav-inner (per R6.6). AUTH_FLOW has no hamburger. */}
          {!isAuthFlow && !isWorkspace && (
            <button
              type="button"
              className="nav-toggle"
              onClick={() => setOpen(!open)}
              aria-label="منو"
              aria-expanded={open ? "true" : "false"}
            >
              <span className="bars"></span>
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

interface NotificationsDropdownProps {
  go: Go;
  notif: UseNotifications;
  onClose: () => void;
}
// R-NAV-5 (spec §3.1.7, §18.1) — the bell dropdown bound to the REAL inbox
// (was 5 hardcoded rows). Clicking a notification marks it read + navigates to
// its mapped destination; «علامت همه خوانده» calls the real read-all endpoint.
const NotificationsDropdown = ({ go, notif, onClose }: NotificationsDropdownProps): React.ReactElement => {
  const { items, unreadCount, loading, error, markRead, markAllRead } = notif;
  const onItemClick = (n: { id: string; type: string; read: boolean }): void => {
    if (!n.read) void markRead(n.id);
    const link = notifLink(n.type);
    onClose();
    if (link) go(link);
  };
  return (
    <div className="dropdown" style={{ width: 380 }}>
      <div className="dropdown-head">
        <h4>اعلان‌ها</h4>
        <span style={{ fontSize: 11, color: "var(--fg-mute)", fontFamily: "var(--f-mono)" }}>
          {unreadCount > 0 ? `${toFa(unreadCount)} نخوانده` : "همه خوانده‌شده"}
        </span>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 420 }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            در حال بارگذاری…
          </div>
        ) : error ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            اعلان‌ها بارگذاری نشد.
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--fg-mute)", fontSize: 13 }}>
            اعلان جدیدی ندارید.
          </div>
        ) : (
          items.map((n) => (
            // a11y: a real <button> so the row is keyboard-focusable + Enter/Space
            // activatable (was a click-only <div>). Reset button chrome; the
            // notif-item class supplies the flex layout.
            <button
              type="button"
              key={n.id}
              onClick={() => onItemClick(n)}
              className="notif-item"
              style={{ cursor: "pointer", width: "100%", display: "flex", textAlign: "right", background: "none", border: "none", font: "inherit", color: "inherit" }}
            >
              <div className="notif-icon"><Icon name="bell" size={14} /></div>
              <div className="flex-1" style={{ minWidth: 0 }}>
                <div className="flex items-center gap-2 mb-1">
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{n.title}</span>
                  {!n.read && <span style={{ width: 6, height: 6, borderRadius: 50, background: "var(--accent)" }}></span>}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--fg-mute)" }}>{n.body}</div>
                {/* a11y: --fg-dim fails 4.5:1 for this small text → --fg-mute. */}
                <div className="mt-1" style={{ fontFamily: "var(--f-mono)", fontSize: 10, color: "var(--fg-mute)" }}>
                  {timeAgo(n.createdAt)}
                </div>
              </div>
            </button>
          ))
        )}
      </div>
      <div className="dropdown-foot">
        <Button variant="ghost" size="sm" disabled={unreadCount === 0} onClick={() => void markAllRead()}>
          علامت‌گذاری همه به‌عنوان خوانده‌شده
        </Button>
        <Button variant="outline" size="sm" onClick={() => { onClose(); go("inbox"); }}>
          صندوق کامل
        </Button>
      </div>
    </div>
  );
};

interface UserDropdownProps {
  go: Go;
  role: Role;
  setRole: (id: RoleId) => void;
  auth: AuthContextValue;
  /** R-NAV-1 — the signed-in user's roles, for the multi-role switcher. */
  availableRoles: RoleId[];
}

const UserDropdown = ({ go, role, setRole, auth, availableRoles }: UserDropdownProps): React.ReactElement => {
  const [loggingOut, setLoggingOut] = React.useState(false);
  const handleLogout = async (e?: React.MouseEvent): Promise<void> => {
    e?.preventDefault?.();
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      if (auth?.logout) {
        await auth.logout();
      }
    } finally {
      // R-NAV-1: clearing the session drops activeRoleId to null on its
      // own (RoleProvider derives from auth), and its reconcile effect
      // removes the persisted preference. We no longer setRole("student")
      // here — there is no "logged-in student" state to fall back to.
      setLoggingOut(false);
      go("login");
    }
  };

  // Phase-14.8: real authed user only, never the mock identity.
  // - If authenticated: full name → email local-part.
  // - If NOT authenticated: role label ("دانشجو" / "استاد" / …) —
  //   never role.name (which is the mock "نسرین رضوی" for student).
  // - Same for identifier: email if authed, else "مهمان" (guest).
  const isAuthed = !!auth?.user;
  const displayName = isAuthed
    ? (auth.user?.fullName || auth.user?.email?.split("@")[0] || role.label)
    : role.label;
  const identifier = isAuthed ? (auth.user?.email ?? role.label) : "مهمان · بدون ورود";
  return (
  <div className="dropdown" style={{ width: 300 }}>
    <div className="p-4.5"  style={{ borderBottom: "1px solid var(--line)"}}>
      <div className="flex items-center gap-3" >
        {/* Phase-A R1.4 B5 — same fix as the trigger above: never leak
            `role.avatar` mock initials to anonymous visitors. Authed →
            the unified deterministic avatar; anonymous → a neutral icon. */}
        {isAuthed && auth.user ? (
          <Avatar
            seed={auth.user.userId}
            role={role.id}
            name={auth.user.fullName || auth.user.email}
            size={44}
          />
        ) : (
          <div className="avatar" style={{ width: 44, height: 44 }} data-anon="true">
            <Icon name="user" size={18} />
          </div>
        )}
        <div className="flex-1"  style={{ minWidth: 0}}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{displayName}</div>
          <div className="mt-0.5"  style={{fontSize: 11, color: "var(--fg-mute)", fontFamily: "var(--f-mono)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{identifier}</div>
        </div>
      </div>
      {/* R-NAV-1: role label pill (real, derived from the session). The
          old `role.subtitle` pill ("ارشد علوم داده") was baked-in mock
          text identical for every user of a role — dropped. */}
      <div className="flex gap-1.5 mt-3" >
        <span className="pill pill-cyan" style={{ fontSize: 10 }}>{role.label}</span>
      </div>
    </div>

    {/* R-NAV-1 (spec §3.1.6) — multi-role switcher. Replaces the old
        demo «view as any role» mock. Shown ONLY when the signed-in user
        actually holds more than one role; switching is restricted to
        availableRoles, so it can never elevate privilege. */}
    {availableRoles.length > 1 && (
      <div className="p-3.5"  style={{ borderBottom: "1px solid var(--line)", background: "var(--surface-2)"}}>
        <div className="mono mb-2.5"  style={{fontSize: 10, color: "var(--fg-mute)", letterSpacing: "0.1em"}}>
          تغییر نقش فعال
        </div>
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${Math.min(availableRoles.length, 5)}, 1fr)` }}>
          {availableRoles.map((rid) => {
            const r = ROLES[rid];
            const isActive = role.id === rid;
            return (
              <button className="rounded-md cursor-pointer flex flex-col items-center gap-1" key={rid} onClick={() => { setRole(rid); go(r.homeRoute); }}
                title={r.label}
                aria-pressed={isActive}
                 style={{padding: "8px 4px",
                  background: isActive ? "var(--accent-soft)" : "var(--surface)",
                  border: "1px solid " + (isActive ? "var(--accent)" : "var(--line)"),
                  fontSize: 10,
                  fontFamily: "inherit",
                  color: isActive ? "var(--accent)" : "var(--fg-mute)"}}>
                <div className={"avatar " + (isActive ? r.color : "")} style={{ width: 22, height: 22, fontSize: 9, opacity: isActive ? 1 : 0.6 }}>{r.avatar}</div>
                {r.label}
              </button>
            );
          })}
        </div>
      </div>
    )}

    <div className="p-1.5" >
      {[
        ["میز کار", "home", role.homeRoute],
        // Phase B R3.a Commit G (D69) — «پروفایل من» per the D69
        // SelfOrAdmin clarification. Points to /profile, the new R3.a
        // self-service page that works for every authenticated role.
        ["پروفایل من", "user", "profile"],
        ["پیام‌ها", "chat", "messages"],
        ["ذخیره‌ها", "star", "bookmarks"],
        ["دستاوردها", "trophy", "achievements"],
        ["گواهی‌ها", "cert", "credential"],
        ["تنظیمات", "settings", "settings"],
        ["پشتیبانی", "headset", "help"],
      ].map(([t, ic, r]) => (
        <button key={t} onClick={() => go(r)} className="dropdown-item">
          <Icon name={ic} size={14} />
          {t}
        </button>
      ))}
    </div>
    <div className="p-1.5"  style={{ borderTop: "1px solid var(--line)"}}>
      <button onClick={handleLogout} disabled={loggingOut} className="dropdown-item" style={{ color: "var(--accent)" }}>
        <Icon name="end" size={14} />
        {loggingOut ? "در حال خروج..." : "خروج از حساب"}
      </button>
    </div>
  </div>
  );
};

export interface FooterProps {
  go: Go;
}

// Per-role workspace link in the footer's "برای کاربران" column.
// Phase-14.7 (F-123): the footer used to list every role's workspace
// link (student desk + instructor console + parent portal + admin
// desk) to every visitor, even unauthenticated ones. A student saw
// links to /instructor /admin /parent and clicking them landed on
// the wrong-role page with no permission check. After Phase-14.7
// router.tsx auth-gates workspace routes, but those links still
// shouldn't be visible to the wrong roles — clicking them just
// bounces to /login, which is confusing UX. Now we show ONLY the
// link for the logged-in user's role, and only when authenticated.
// R-NAV-2: each role's primary workspace = its real home route (matches
// role.homeRoute). Footer surfaces only the logged-in user's entry. Mock
// /instructor + /authoring destinations removed; every route here is real
// (faculty-dashboard is a titled ComingSoon placeholder, not a dead link).
const ROLE_WORKSPACE_LINK: Record<RoleId, { route: string; label: string }> = {
  student: { route: "dashboard", label: "میز کار دانشجو" },
  instructor: { route: "progress", label: "میز کار استاد" },
  admin: { route: "admin", label: "میز مدیریت" },
  finance_manager: { route: "admin/finance/treasury", label: "میز مالی" },
  faculty_admin: { route: "faculty-dashboard", label: "داشبورد دانشکده" },
  parent: { route: "parent", label: "پورتال والد" },
  org: { route: "org", label: "میز سازمان" },
  ta: { route: "ta", label: "میز دستیار" },
  content_manager: { route: "content", label: "میز محتوا" },
  // S-NRI-ADAPT — «سازندهٔ پکیج».
  creator: { route: "creator-studio", label: "استودیو دانش" },
  support: { route: "support", label: "میز پشتیبانی" },
  moderator: { route: "moderate", label: "میز نظارت" },
  super_admin: { route: "super", label: "میز ابرمدیر" },
  // WELL-4 — counselor (مشاور دانشگاه) primary workspace = the counselor inbox.
  counselor: { route: "counselor-inbox", label: "میز کار مشاور" },
  // S-NRI-3 — org/oversight roles. Primary surface = the org structure (or the
  // role's read surface); every route here is reachable for that role.
  holding_manager: { route: "admin/organizations", label: "میز مدیر هلدینگ" },
  company_admin: { route: "admin/organizations", label: "میز ادمین شرکت" },
  company_training_manager: { route: "admin/organizations", label: "میز آموزش شرکت" },
  certification_officer: { route: "credential", label: "صدور گواهی" },
  practical_assessor: { route: "my-courses", label: "ارزیابی عملی" },
  ministry_supervisor: { route: "admin/organizations", label: "میز ناظر وزارت" },
  auditor: { route: "admin/organizations", label: "میز حسابرس" },
};

export const Footer = ({ go }: FooterProps): React.ReactElement => {
  const auth = useAuth();
  const { role } = useRole();
  const isAuthed = auth.isAuthenticated;
  const myWorkspace = ROLE_WORKSPACE_LINK[role.id];
  return (
  <footer className="footer">
    <div className="shell">
      <div className="footer-grid">
        <div>
          <div className="brand mb-4.5" >
            {/* Footer brand uses the institutional logo.
                Footer has a dark navy background, so the mark must stay
                visible on dark (the NRI mark works on both). */}
            <img
              src="/tenants/nri/pajooheshgah-192.webp"
              alt="پژوهشگاه نیرو"
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
              style={{ width: 40, height: 40, objectFit: "contain", display: "block" }}
            />
            <span>پژوهشگاه نیرو
              <div className="brand-sub">آموزش ضمن خدمت صنعت آب و برق</div>
            </span>
          </div>
          {/* R7.3 A.2.i — drop the inline `color: var(--fg-mute)`.
              The footer-on-dark rule `.footer p { color: rgba(255,255,255,0.7) }`
              gives ~9.66:1 against the navy footer bg (AAA). The
              inline `--fg-mute` (#4a5a76) was 2.77:1 — Lighthouse fail. */}
          <p style={{ fontSize: 14, lineHeight: 1.7, maxWidth: 360 }}>
            یک زیرساخت آموزشی کاملاً دیجیتال که در آن هوش مصنوعی نه یک افزونه، بلکه شالوده‌ی یادگیری، طراحی برنامه و سنجش است.
          </p>
          <div className="standards mt-5" >
            <span className="std">LTI 1.3</span>
            <span className="std">xAPI</span>
            <span className="std">QTI</span>
            <span className="std">Caliper</span>
            <span className="std">WCAG 2.2</span>
          </div>
        </div>
        <div>
          {/* R7.3 A.3.ii — footer column headers were `<h5>` directly
              after the page's last `<h2>` (heading-order skip). Renamed
              to `<h3>`; CSS rule `.footer h5` was renamed to `.footer h3`
              in styles.css to preserve the visual treatment. */}
          <h3>محصول</h3>
          {/* R-NAV-6 (spec §3.8): auth-aware. Anonymous sees ONLY public
              links — never a workspace feature that bounces to /login. */}
          <ul>
            {isAuthed ? (
              <>
                <li><a href="/classroom" onClick={(e)=>{e.preventDefault();go("classroom")}}>کلاس برخط</a></li>
                <li><a href="/labs" onClick={(e)=>{e.preventDefault();go("labs")}}>آزمایشگاه‌های مجازی</a></li>
                <li><a href="/recordings" onClick={(e)=>{e.preventDefault();go("recordings")}}>آرشیو ضبط‌ها</a></li>
                <li><a href="/search" onClick={(e)=>{e.preventDefault();go("search")}}>جستجوی معنایی</a></li>
                <li><a href="/assessment" onClick={(e)=>{e.preventDefault();go("assessment")}}>آزمون‌ها و ارزشیابی</a></li>
              </>
            ) : (
              <>
                <li><a href="/programs" onClick={(e)=>{e.preventDefault();go("programs")}}>برنامه‌ها و مقاطع</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>درخواست پذیرش</a></li>
                <li><a href="/track" onClick={(e)=>{e.preventDefault();go("track")}}>پیگیری درخواست</a></li>
                <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>درباره‌ی ما</a></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <h3>دانشگاه</h3>
          <ul>
            {isAuthed ? (
              <>
                <li><a href="/schools" onClick={(e)=>{e.preventDefault();go("schools")}}>دانشکده‌ها</a></li>
                <li><a href="/faculty" onClick={(e)=>{e.preventDefault();go("faculty")}}>هیات علمی</a></li>
                <li><a href="/research" onClick={(e)=>{e.preventDefault();go("research")}}>پژوهش و دکتری</a></li>
                <li><a href="/library" onClick={(e)=>{e.preventDefault();go("library")}}>کتابخانه دیجیتال</a></li>
                <li><a href="/events" onClick={(e)=>{e.preventDefault();go("events")}}>رویدادها</a></li>
              </>
            ) : (
              <>
                <li><a href="/programs" onClick={(e)=>{e.preventDefault();go("programs")}}>دانشکده‌ها و برنامه‌ها</a></li>
                <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>درباره‌ی دانشگاه</a></li>
                <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>منشور صداقت علمی</a></li>
                <li><a href="/help" onClick={(e)=>{e.preventDefault();go("help")}}>راهنما و پشتیبانی</a></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <h3>برای کاربران</h3>
          <ul>
            {isAuthed ? (
              <>
                {/* Phase-14.7: only the logged-in user's own workspace
                    link. Other roles' portals would auth-redirect to
                    /login anyway, so showing them here was misleading. */}
                <li><a href={"/" + myWorkspace.route} onClick={(e)=>{e.preventDefault();go(myWorkspace.route)}}>{myWorkspace.label}</a></li>
                <li><a href="/community" onClick={(e)=>{e.preventDefault();go("community")}}>جامعه</a></li>
                <li><a href="/credential" onClick={(e)=>{e.preventDefault();go("credential")}}>گواهی دیجیتال</a></li>
                <li><a href="/settings" onClick={(e)=>{e.preventDefault();go("settings")}}>تنظیمات</a></li>
              </>
            ) : (
              <>
                {/* Unauthenticated visitors see public CTAs, not
                    role-specific portal links. */}
                <li><a href="/login" onClick={(e)=>{e.preventDefault();go("login")}}>ورود به حساب</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>ساخت حساب</a></li>
                <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>درخواست پذیرش</a></li>
                <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>صداقت علمی</a></li>
              </>
            )}
          </ul>
        </div>
        <div>
          <h3>منابع</h3>
          <ul>
            {/* «درخواست پذیرش» → the real public /apply funnel. PUBLIC route, so
                it never dead-ends for any visitor (anon or authed) — kept here in
                the always-visible منابع column so it's reachable from every page. */}
            <li><a href="/apply" onClick={(e)=>{e.preventDefault();go("apply")}}>درخواست پذیرش</a></li>
            <li><a href="/pricing" onClick={(e)=>{e.preventDefault();go("pricing")}}>پلن‌ها و قیمت</a></li>
            <li><a href="/help" onClick={(e)=>{e.preventDefault();go("help")}}>مرکز پشتیبانی</a></li>
            <li><a href="/about" onClick={(e)=>{e.preventDefault();go("about")}}>درباره ما</a></li>
            {/* R-NAV-6: Office Hours is a workspace route → authed only; anon
                gets the public honor-code instead of a /login bounce. */}
            {isAuthed ? (
              <li><a href="/officehours" onClick={(e)=>{e.preventDefault();go("officehours")}}>Office Hours</a></li>
            ) : (
              <li><a href="/honor-code" onClick={(e)=>{e.preventDefault();go("honor-code")}}>منشور صداقت علمی</a></li>
            )}
            <li><a href="#">وضعیت سرویس</a></li>
            <li><a href="#">API و توسعه‌دهندگان</a></li>
          </ul>
        </div>
      </div>
      {/* Phase-A R1.3: organizational attribution. سامانهٔ آموزش ضمن خدمت
          صنعت آب و برق is the product; ownership and copyright sit with
          پژوهشگاه نیرو (زیر نظر وزارت نیرو). */}
      <OrgAttribution variant="full" className="footer-org-attribution" />
      <div className="footer-bot">
        <span>پژوهشگاه نیرو · سامانهٔ آموزش ضمن خدمت صنعت آب و برق</span>
        <span>v1.0.0 · build 2026.05 · region: TEH-01</span>
      </div>
    </div>
  </footer>
  );
};

// =====================================================
// Persian numerals helper
// =====================================================
export const toFa = (n: number | string): string =>
  String(n).replace(/[0-9]/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[Number(d)]);

// R-NAV-5 — relative Persian timestamp for notifications ("۲ ساعت پیش").
// Browser-only (Date.now is fine here; it's unavailable only in workflow
// scripts). Defined module-level; the dropdown calls it lazily at render.
export const timeAgo = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diffMin = Math.max(0, Math.floor((Date.now() - then) / 60000));
  if (diffMin < 1) return "همین الان";
  if (diffMin < 60) return `${toFa(diffMin)} دقیقه پیش`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${toFa(h)} ساعت پیش`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${toFa(d)} روز پیش`;
  const mo = Math.floor(d / 30);
  return `${toFa(mo)} ماه پیش`;
};

// =====================================================
// Sparkline mini-chart (SVG)
// =====================================================
export interface SparklineProps {
  values: number[];
  color?: string;
  height?: number;
  width?: number;
}
export const Sparkline = ({
  values,
  color = "var(--cyan)",
  height = 50,
  width = 220,
}: SparklineProps): React.ReactElement => {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height * 0.85 - height * 0.07;
    return [x, y];
  });
  const d = "M " + pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L ");
  const dArea = d + ` L ${width},${height} L 0,${height} Z`;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sg-${color.replace(/[^a-z]/gi, "")}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.4" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={dArea} fill={`url(#sg-${color.replace(/[^a-z]/gi, "")})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// =====================================================
// Cognitive radar (SVG) — for student profile
// =====================================================
export interface CognitiveRadarProps {
  values: number[];
  labels: string[];
  size?: number;
}
export const CognitiveRadar = ({ values, labels, size = 280 }: CognitiveRadarProps): React.ReactElement => {
  const cx = size / 2, cy = size / 2;
  const radius = size * 0.4;
  const n = values.length;
  const angle = (i: number): number => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i: number, r: number): [number, number] => [cx + Math.cos(angle(i)) * r, cy + Math.sin(angle(i)) * r];

  const rings = [0.25, 0.5, 0.75, 1].map((f) => {
    const pts = Array.from({ length: n }, (_, i) => point(i, radius * f).join(",")).join(" ");
    return <polygon key={f} points={pts} fill="none" stroke="var(--line-2)" strokeWidth="1" />;
  });
  const spokes = Array.from({ length: n }, (_, i) => {
    const [x, y] = point(i, radius);
    return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="var(--line-2)" strokeWidth="1" />;
  });
  const valuePts = values.map((v, i) => point(i, radius * (v / 100)).join(",")).join(" ");

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {rings}
      {spokes}
      <polygon points={valuePts} fill="oklch(0.82 0.16 195 / 0.18)" stroke="var(--cyan)" strokeWidth="2" strokeLinejoin="round" />
      {values.map((v, i) => {
        const [x, y] = point(i, radius * (v / 100));
        return <circle key={i} cx={x} cy={y} r={3.5} fill="var(--cyan)" />;
      })}
      {labels.map((l, i) => {
        const [x, y] = point(i, radius + 26);
        return (
          <text key={i} x={x} y={y} textAnchor="middle" dominantBaseline="middle"
            fill="var(--fg-mute)" fontSize="11" fontFamily="Vazirmatn, sans-serif" fontWeight="500">
            {l}
          </text>
        );
      })}
    </svg>
  );
};

// =====================================================
// Knowledge Graph visualization (SVG)
// =====================================================
export const KnowledgeGraph = (): React.ReactElement => {
  const nodes = [
    { id: "ml", x: 50, y: 40, r: 36, kind: "core", label: "یادگیری ماشین" },
    { id: "sup", x: 22, y: 22, r: 24, kind: "topic", label: "نظارت‌شده" },
    { id: "uns", x: 78, y: 22, r: 24, kind: "topic", label: "بدون‌نظارت" },
    { id: "rl",  x: 50, y: 76, r: 24, kind: "topic", label: "تقویتی" },
    { id: "lr",  x: 12, y: 50, r: 18, kind: "sub", label: "رگرسیون" },
    { id: "svm", x: 28, y: 70, r: 18, kind: "sub", label: "SVM" },
    { id: "nn",  x: 88, y: 50, r: 18, kind: "sub", label: "شبکه عصبی" },
    { id: "cl",  x: 70, y: 8,  r: 18, kind: "sub", label: "خوشه‌بندی" },
    { id: "q",   x: 70, y: 92, r: 18, kind: "sub", label: "Q-learning" },
  ];
  const edges = [
    ["ml", "sup"], ["ml", "uns"], ["ml", "rl"],
    ["sup", "lr"], ["sup", "svm"], ["sup", "nn"],
    ["uns", "cl"], ["uns", "nn"], ["rl", "q"],
  ];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const colorFor = (kind: string): string =>
    kind === "core" ? "var(--cyan)" : kind === "topic" ? "var(--amber)" : "var(--violet)";

  return (
    <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
      <defs>
        <radialGradient id="node-glow">
          <stop offset="0%" stopColor="var(--cyan)" stopOpacity="0.4" />
          <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {edges.map(([a, b], i) => {
        const A = byId[a], B = byId[b];
        return <line key={i} x1={A.x} y1={A.y} x2={B.x} y2={B.y}
          stroke={"color-mix(in oklch, " + colorFor(A.kind) + " 50%, transparent)"}
          strokeWidth="0.3" />;
      })}
      {nodes.map((n) => (
        <g key={n.id}>
          {n.kind === "core" && <circle cx={n.x} cy={n.y} r={n.r / 4} fill="url(#node-glow)" opacity="0.6" />}
          <circle cx={n.x} cy={n.y} r={n.r / 8} fill={colorFor(n.kind)} stroke={colorFor(n.kind)} strokeOpacity="0.4" strokeWidth={n.r / 8} fillOpacity="0.5" />
          <text x={n.x} y={n.y + n.r / 4 + 2} textAnchor="middle" fill="var(--fg)" fontSize="2.2" fontFamily="Vazirmatn">
            {n.label}
          </text>
        </g>
      ))}
    </svg>
  );
};

// =====================================================
// 3D-ish architecture diagram (isometric stack)
// =====================================================
export const ArchStack = (): React.ReactElement => {
  const layers = [
    { name: "Experience Layer", sub: "وب · موبایل · کلاس برخط · داشبورد", color: "var(--cyan)" },
    { name: "Agent Orchestration", sub: "AI Tutor · Coach · Critic · Mentor · Grader", color: "var(--violet)" },
    { name: "Domain Services", sub: "Course · Enrollment · Gradebook · Credential", color: "var(--amber)" },
    { name: "Data & Learning Record Store", sub: "PostgreSQL · ClickHouse · Vector · Object", color: "var(--fg-mute)" },
    { name: "Cloud-Native Platform", sub: "Kubernetes · Kafka · CDN · Observability", color: "var(--fg-dim)" },
  ];
  return (
    <div
      // Phase-16 R11 — the 3D rotation (rotateX(48deg) rotateZ(-28deg))
      // projects the 320-px boxes to a visual width ~360 px, which on
      // 320-px viewports leaked past the page-shell. Clip horizontally
      // at the ArchStack parent so the diagram still renders inside
      // the slot.
      style={{
        perspective: 1400,
        transformStyle: "preserve-3d",
        padding: "20px 0",
        overflowX: "clip",
        maxWidth: "100%",
      }}
    >
      <div
        className="flex flex-col gap-4.5 items-center"
        style={{
          transform: "rotateX(48deg) rotateZ(-28deg)",
          transformStyle: "preserve-3d",
        }}
      >
        {layers.map((L, i) => (
          <div
            className="rounded-xl relative flex flex-col justify-center"
            key={i}
            style={{
              // Cap at 320px on desktop, fluid below — the 3D tilt
              // never visually exceeds the slot now.
              width: "min(320px, calc(100% - 16px))",
              height: 70,
              background: "linear-gradient(180deg, var(--surface-2), var(--surface))",
              border: "1px solid var(--line-2)",
              padding: "0 18px",
              transform: `translateZ(${(layers.length - i) * 20}px)`,
              boxShadow: "0 20px 40px -20px rgba(0,0,0,0.7)",
            }}
          >
            <div
              className="absolute"
              style={{
                top: 8,
                left: 12,
                width: 5,
                height: 5,
                borderRadius: 50,
                background: L.color,
                boxShadow: `0 0 12px ${L.color}`,
              }}
            />
            <div style={{ fontSize: 14, fontWeight: 600 }}>{L.name}</div>
            <div className="mt-1" style={{ fontFamily: "var(--f-mono)", fontSize: 10, color: "var(--fg-mute)" }}>{L.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// (exports above)
